import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Dropdown,
  Form,
  Grid,
  Header,
  Icon,
  Label,
  Message,
  Modal,
  Segment,
  Table,
} from "semantic-ui-react";
import Swal from "sweetalert2";

import { auth, db } from "../firebase/firebaseConfig";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
} from "firebase/firestore";

import { UserContext } from "../context/UserContext";
import ProtocoloDB from "../firebase/ProtocoloDB";
import { Programas } from "./TipDoc";

const CATEGORIAS = [
  { key: "INSTRUMENTOS", titulo: "Instrumentos", icon: "wrench" },
  { key: "CHECK", titulo: "Checklist", icon: "check square outline" },
  { key: "MEDICION", titulo: "Medición", icon: "chart line" },
  { key: "SEGURIDAD", titulo: "Seguridad", icon: "shield" },
];

const cardStyle = {
  borderRadius: 10,
  border: "1px solid #e5e9ef",
  boxShadow: "0 2px 8px rgba(9,30,66,.06)",
  marginBottom: 18,
};

const activeCardStyle = {
  ...cardStyle,
  border: "1px solid #9fd8d8",
  boxShadow: "0 2px 10px rgba(0,128,128,.08)",
};

const stickyHeader = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  background: "#f7f9fb",
  color: "#52606d",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  whiteSpace: "nowrap",
};

const Protocolos = () => {
  const user = auth.currentUser;
  const { users } = useContext(UserContext);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: "", msg: "" });

  const [familias, setFamilias] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [items, setItems] = useState([]);
  const [protocolo, setProtocolo] = useState([]);
  const [protocolCabPend, setProtocolCabPend] = useState([]);
  const [protocolCabConf, setProtocolCabConf] = useState([]);

  const [nomFamilia, setNomFamilia] = useState("");
  const [nomTipo, setNomTipo] = useState("");
  const [programa, setPrograma] = useState("");
  const [selectedItemId, setSelectedItemId] = useState("");

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState("");

  const [verModalOpen, setVerModalOpen] = useState(false);
  const [verProt, setVerProt] = useState([]);
  const [verLoading, setVerLoading] = useState(false);

  const setOk = (msg) => setToast({ type: "ok", msg });
  const setErr = (msg) => setToast({ type: "err", msg });
  const resetToast = () => setToast({ type: "", msg: "" });

  const diasFromPrograma = (p) => {
    const value = String(p || "").trim().toUpperCase();
    if (value === "ANUAL") return 365;
    if (value === "SEMESTRAL") return 180;
    if (value === "TRIMESTRAL") return 90;
    if (value === "MENSUAL") return 30;
    if (value === "QUINCENAL") return 15;
    return 0;
  };

  const familiasOptions = useMemo(
    () =>
      [...familias]
        .sort((a, b) =>
          String(a.familia || "").localeCompare(String(b.familia || ""), "es")
        )
        .map((x) => ({
          key: x.id,
          value: x.familia,
          text: x.familia,
        })),
    [familias]
  );

  const tiposOptions = useMemo(
    () =>
      [...tipos]
        .sort((a, b) =>
          String(a.tipo || "").localeCompare(String(b.tipo || ""), "es")
        )
        .map((x) => ({
          key: x.id,
          value: x.tipo,
          text: x.tipo,
        })),
    [tipos]
  );

  const programasOptions = useMemo(
    () =>
      (Programas || []).map((p) => ({
        key: p.key ?? p.text,
        value: p.text,
        text: p.text,
      })),
    []
  );

  const itemOptions = useMemo(
    () =>
      [...items]
        .sort((a, b) =>
          String(a.nombre || "").localeCompare(String(b.nombre || ""), "es")
        )
        .map((x) => ({
          key: x.id,
          value: x.id,
          text: x.nombre,
          description: x.categoria,
        })),
    [items]
  );

  const ordenarItems = (arr) => {
    const todosConOrden =
      arr.length > 0 &&
      arr.every((x) => Number.isFinite(Number(x.orden)));

    if (todosConOrden) {
      return [...arr].sort(
        (a, b) => Number(a.orden) - Number(b.orden)
      );
    }

    const toMillis = (v) => {
      if (v?.toMillis) return v.toMillis();
      if (v?.seconds) return v.seconds * 1000;
      if (v instanceof Date) return v.getTime();
      return 0;
    };

    return [...arr].sort(
      (a, b) =>
        toMillis(a.fechaAdd ?? a.fechaadd ?? a.fechamod) -
        toMillis(b.fechaAdd ?? b.fechaadd ?? b.fechamod)
    );
  };

  const porCategoria = (lista, categoria) =>
    ordenarItems(lista.filter((x) => x.categoria === categoria));

  const getFamilias = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "familias"),
      where("emp_id", "==", users.emp_id),
      where("activo", "==", true)
    );

    const snap = await getDocs(q);
    setFamilias(
      snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }))
    );
  }, [users?.emp_id]);

  const getTipos = useCallback(async () => {
    if (!users?.emp_id || !nomFamilia) {
      setTipos([]);
      return;
    }

    const q = query(
      collection(db, "tipos"),
      where("emp_id", "==", users.emp_id),
      where("activo", "==", true),
      where("familia", "==", nomFamilia)
    );

    const snap = await getDocs(q);
    setTipos(
      snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }))
    );
  }, [users?.emp_id, nomFamilia]);

  const getItems = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "items"),
      where("emp_id", "==", users.emp_id)
    );

    const snap = await getDocs(q);
    setItems(
      snap.docs.map((d) => ({
        ...d.data(),
        id: d.id,
      }))
    );
  }, [users?.emp_id]);

  const consultarCabProtPend = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "protocoloscab"),
      where("emp_id", "==", users.emp_id),
      where("confirmado", "==", false)
    );

    const snap = await getDocs(q);

    const arr = snap.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));

    arr.sort((a, b) =>
      String(a.tipo || "").localeCompare(String(b.tipo || ""), "es")
    );

    setProtocolCabPend(arr);
  }, [users?.emp_id]);

  const consultarCabProtConf = useCallback(async () => {
    if (!users?.emp_id) return;

    const q = query(
      collection(db, "protocoloscab"),
      where("emp_id", "==", users.emp_id),
      where("confirmado", "==", true)
    );

    const snap = await getDocs(q);

    const arr = snap.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));

    arr.sort((a, b) =>
      String(a.tipo || "").localeCompare(String(b.tipo || ""), "es")
    );

    setProtocolCabConf(arr);
  }, [users?.emp_id]);

  const buscarCabeceraPendiente = useCallback(async () => {
    if (!users?.emp_id || !nomFamilia || !nomTipo || !programa) {
      return [];
    }

    const q = query(
      collection(db, "protocoloscab"),
      where("emp_id", "==", users.emp_id),
      where("familia", "==", nomFamilia),
      where("tipo", "==", nomTipo),
      where("programa", "==", programa),
      where("confirmado", "==", false)
    );

    const snap = await getDocs(q);

    return snap.docs.map((d) => ({
      ...d.data(),
      id: d.id,
    }));
  }, [users?.emp_id, nomFamilia, nomTipo, programa]);

  const consultarProtocolos = useCallback(async () => {
    if (!users?.emp_id || !nomFamilia || !nomTipo || !programa) {
      setProtocolo([]);
      return;
    }

    try {
      const cabArr = await buscarCabeceraPendiente();

      if (cabArr.length !== 1) {
        setProtocolo([]);
        return;
      }

      const q = query(
        collection(db, "protocolos"),
        where("emp_id", "==", users.emp_id),
        where("cab_id", "==", cabArr[0].id)
      );

      const snap = await getDocs(q);

      setProtocolo(
        snap.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }))
      );
    } catch (e) {
      console.error(e);
      setProtocolo([]);
    }
  }, [
    users?.emp_id,
    nomFamilia,
    nomTipo,
    programa,
    buscarCabeceraPendiente,
  ]);

  const loadAll = useCallback(async () => {
    if (!users?.emp_id) return;

    setLoading(true);

    try {
      await Promise.all([
        getFamilias(),
        getItems(),
        consultarCabProtPend(),
        consultarCabProtConf(),
      ]);
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        "No se pudo cargar la información.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, [
    users?.emp_id,
    getFamilias,
    getItems,
    consultarCabProtPend,
    consultarCabProtConf,
  ]);

  const addCabProtocolo = async () => {
    resetToast();

    if (!users?.emp_id) return;

    if (!nomFamilia) {
      return setErr("Selecciona Familia.");
    }

    if (!nomTipo) {
      return setErr("Selecciona Tipo.");
    }

    if (!programa) {
      return setErr("Selecciona Programa.");
    }

    const existeConfirmado = protocolCabConf.some(
      (x) =>
        x.familia === nomFamilia &&
        x.tipo === nomTipo &&
        x.programa === programa
    );

    if (existeConfirmado) {
      return setErr(
        "Ya existe un protocolo confirmado para esta Familia / Tipo / Programa."
      );
    }

    const existePendiente = protocolCabPend.find(
      (x) =>
        x.familia === nomFamilia &&
        x.tipo === nomTipo &&
        x.programa === programa
    );

    if (existePendiente) {
      return setErr(
        "Ya existe un protocolo pendiente. Cárgalo desde Protocolos por terminar."
      );
    }

    const dias = diasFromPrograma(programa);

    if (!dias) {
      return setErr(
        `El programa "${programa}" no tiene una cantidad de días configurada.`
      );
    }

    setSaving(true);

    try {
      const fecha = new Date();

      await addDoc(
        collection(db, "protocoloscab"),
        {
          nombre: `PAUTA DE MANTENCIÓN ${programa}`,
          familia: nomFamilia,
          tipo: nomTipo,
          programa,
          dias,
          confirmado: false,
          useradd: user?.email || "",
          usermod: user?.email || "",
          fechaadd: fecha,
          fechamod: fecha,
          emp_id: users.emp_id,
        }
      );

      await consultarCabProtPend();
      setProtocolo([]);

      setOk(
        "Cabecera creada. Ahora agrega los ítems de la pauta."
      );
    } catch (e) {
      console.error(e);

      Swal.fire(
        "Error",
        "No se pudo crear la cabecera.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const nuevo = () => {
    resetToast();
    setNomFamilia("");
    setNomTipo("");
    setPrograma("");
    setTipos([]);
    setProtocolo([]);
    setSelectedItemId("");
  };

  const cargarPendiente = (cab) => {
    resetToast();

    setNomFamilia(cab.familia);
    setNomTipo(cab.tipo);
    setPrograma(cab.programa);
    setSelectedItemId("");

    setOk(
      "Protocolo pendiente cargado. Puedes continuar agregando ítems."
    );

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const agregarItem = async (itemId) => {
    resetToast();

    if (!users?.emp_id || !itemId) return;

    if (!nomFamilia || !nomTipo || !programa) {
      return setErr(
        "Primero selecciona Familia / Tipo / Programa."
      );
    }

    const itemRow = items.find(
      (x) => x.id === itemId
    );

    if (!itemRow) {
      return setErr("Ítem no encontrado.");
    }

    if (
      protocolo.some(
        (x) => x.item_id === itemRow.id
      )
    ) {
      setSelectedItemId("");
      return setErr(
        "Este ítem ya está agregado al protocolo."
      );
    }

    setSaving(true);

    try {
      const cabArr =
        await buscarCabeceraPendiente();

      if (cabArr.length === 0) {
        setSelectedItemId("");

        return setErr(
          "No existe una cabecera pendiente. Primero crea o carga el protocolo."
        );
      }

      if (cabArr.length > 1) {
        setSelectedItemId("");

        return setErr(
          "Existe más de una cabecera pendiente para este protocolo."
        );
      }

      const cab = cabArr[0];
      const fecha = new Date();

      await ProtocoloDB({
        nombre: cab.nombre,
        familia: cab.familia,
        tipo: cab.tipo,
        programa: cab.programa,
        dias: cab.dias,
        item: itemRow.nombre,
        item_id: itemRow.id,
        categoria: itemRow.categoria,
        medida: itemRow.medida,
        inicial: parseFloat(itemRow.inicial),
        final: parseFloat(itemRow.final),
        cab_id: cab.id,
        userAdd: user?.email || "",
        userMod: user?.email || "",
        fechaAdd: fecha,
        fechaMod: fecha,
        emp_id: users.emp_id,
        confirmado: false,
      });

      setSelectedItemId("");

      await consultarProtocolos();

      setOk("Ítem agregado correctamente.");
    } catch (e) {
      console.error(e);

      Swal.fire(
        "Error",
        "No se pudo agregar el ítem.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmarProtocolo = async () => {
    resetToast();

    if (!users?.emp_id) return;

    if (!nomFamilia || !nomTipo || !programa) {
      return setErr(
        "Selecciona Familia / Tipo / Programa."
      );
    }

    if (protocolo.length === 0) {
      return Swal.fire(
        "Atención",
        "No hay ítems agregados para confirmar.",
        "warning"
      );
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Confirmar protocolo",
      text:
        "Una vez confirmado quedará disponible para su utilización en Servicio Técnico.",
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#00b5ad",
    });

    if (!result.isConfirmed) return;

    setSaving(true);

    try {
      const cabArr =
        await buscarCabeceraPendiente();

      if (cabArr.length === 0) {
        return Swal.fire(
          "Error",
          "No se encontró la cabecera pendiente.",
          "error"
        );
      }

      if (cabArr.length > 1) {
        return Swal.fire(
          "Atención",
          "Existe más de una cabecera pendiente para este protocolo.",
          "warning"
        );
      }

      const cab = cabArr[0];
      const fecha = new Date();

      await updateDoc(
        doc(db, "protocoloscab", cab.id),
        {
          confirmado: true,
          usermod: user?.email || "",
          fechamod: fecha,
        }
      );

      const detalles = protocolo.filter(
        (x) => x.cab_id === cab.id
      );

      await Promise.all(
        detalles.map((x) =>
          updateDoc(
            doc(db, "protocolos", x.id),
            {
              confirmado: true,
              userMod: user?.email || "",
              fechaMod: fecha,
            }
          )
        )
      );

      await Promise.all([
        consultarCabProtPend(),
        consultarCabProtConf(),
      ]);

      nuevo();

      setOk(
        "Protocolo confirmado exitosamente."
      );
    } catch (e) {
      console.error(e);

      Swal.fire(
        "Error",
        "No se pudo confirmar el protocolo.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const borrarItem = async () => {
    if (!deleteTargetId) return;

    setSaving(true);

    try {
      await deleteDoc(
        doc(db, "protocolos", deleteTargetId)
      );

      await consultarProtocolos();

      setOk("Ítem eliminado.");
    } catch (e) {
      console.error(e);

      Swal.fire(
        "Error",
        "No se pudo eliminar el ítem.",
        "error"
      );
    } finally {
      setSaving(false);
      setDeleteModalOpen(false);
      setDeleteTargetId("");
    }
  };

  const verProtocoloConfirmado = async (cabId) => {
    setVerProt([]);
    setVerModalOpen(true);
    setVerLoading(true);

    try {
      const q = query(
        collection(db, "protocolos"),
        where("emp_id", "==", users.emp_id),
        where("cab_id", "==", cabId)
      );

      const snap = await getDocs(q);

      setVerProt(
        snap.docs.map((d) => ({
          ...d.data(),
          id: d.id,
        }))
      );
    } catch (e) {
      console.error(e);

      Swal.fire(
        "Error",
        "No se pudo cargar el protocolo.",
        "error"
      );
    } finally {
      setVerLoading(false);
    }
  };

  useEffect(() => {
    if (users?.emp_id) {
      loadAll();
    }
  }, [users?.emp_id, loadAll]);

  useEffect(() => {
    setNomTipo("");
    setTipos([]);

    if (nomFamilia) {
      getTipos();
    }
  }, [nomFamilia, getTipos]);

  useEffect(() => {
    consultarProtocolos();
  }, [consultarProtocolos]);

  const cabeceraPendiente = protocolCabPend.some(
    (x) =>
      x.familia === nomFamilia &&
      x.tipo === nomTipo &&
      x.programa === programa
  );

  const renderTablaItems = (
    lista,
    editable = false
  ) => {
    const esMedicion =
      lista[0]?.categoria === "MEDICION";

    return (
      <div style={{ overflowX: "auto" }}>
        <Table
          celled
          compact="very"
          selectable
          style={{
            margin: 0,
            fontSize: 12,
            borderRadius: 6,
          }}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                style={stickyHeader}
                width={1}
                textAlign="center"
              >
                #
              </Table.HeaderCell>

              <Table.HeaderCell
                style={stickyHeader}
              >
                Ítem
              </Table.HeaderCell>

              {esMedicion && (
                <>
                  <Table.HeaderCell
                    style={stickyHeader}
                    width={2}
                  >
                    Unidad
                  </Table.HeaderCell>

                  <Table.HeaderCell
                    style={stickyHeader}
                    width={3}
                  >
                    Rango
                  </Table.HeaderCell>
                </>
              )}

              {editable && (
                <Table.HeaderCell
                  style={stickyHeader}
                  width={1}
                  textAlign="center"
                >
                  Acción
                </Table.HeaderCell>
              )}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {lista.map((it, idx) => (
              <Table.Row key={it.id}>
                <Table.Cell
                  textAlign="center"
                  style={{ color: "#6b778c" }}
                >
                  {idx + 1}
                </Table.Cell>

                <Table.Cell
                  style={{
                    whiteSpace: "normal",
                    fontWeight: 500,
                  }}
                >
                  {it.item}
                </Table.Cell>

                {esMedicion && (
                  <>
                    <Table.Cell>
                      {it.medida || "—"}
                    </Table.Cell>

                    <Table.Cell>
                      {Number.isFinite(
                        Number(it.inicial)
                      ) &&
                      Number.isFinite(
                        Number(it.final)
                      )
                        ? `${it.inicial} - ${it.final}`
                        : "—"}
                    </Table.Cell>
                  </>
                )}

                {editable && (
                  <Table.Cell textAlign="center">
                    <Button
                      basic
                      compact
                      icon
                      size="mini"
                      title="Eliminar ítem"
                      disabled={saving}
                      onClick={() => {
                        setDeleteTargetId(it.id);
                        setDeleteModalOpen(true);
                      }}
                      style={{
                        color: "#db2828",
                        background: "#fff",
                        boxShadow: "none",
                      }}
                    >
                      <Icon
                        name="trash alternate outline"
                        color="red"
                        style={{ margin: 0 }}
                      />
                    </Button>
                  </Table.Cell>
                )}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  };

  const renderCategorias = (
    lista,
    editable = false
  ) => (
    <>
      {CATEGORIAS.map((cat) => {
        const datos = porCategoria(
          lista,
          cat.key
        );

        if (!datos.length) return null;

        return (
          <Segment
            key={cat.key}
            style={{
              borderRadius: 8,
              border: "1px solid #e5e9ef",
              boxShadow: "none",
              padding: 0,
              overflow: "hidden",
              marginBottom: 14,
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                background: "#f8fafc",
                borderBottom:
                  "1px solid #e5e9ef",
              }}
            >
              <Header
                as="h4"
                style={{
                  margin: 0,
                  color: "#253858",
                }}
              >
                <Icon
                  name={cat.icon}
                  style={{
                    color: "#00a8a8",
                  }}
                />

                <Header.Content>
                  {cat.titulo}

                  <Label
                    basic
                    size="mini"
                    style={{
                      marginLeft: 8,
                      color: "#6b778c",
                    }}
                  >
                    {datos.length}
                  </Label>
                </Header.Content>
              </Header>
            </div>

            <div style={{ padding: 12 }}>
              {renderTablaItems(
                datos,
                editable
              )}
            </div>
          </Segment>
        );
      })}
    </>
  );

  return (
    <Container
      fluid
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: "20px 16px 40px",
      }}
    >
      <Container
        fluid
        style={{
          maxWidth: 1400,
          margin: "0 auto",
        }}
      >
        {/* CABECERA */}
        <Segment
          style={{
            ...cardStyle,
            padding: "18px 20px",
          }}
        >
          <Grid
            verticalAlign="middle"
            stackable
          >
            <Grid.Column width={12}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                }}
              >
                <div
                  style={{
                    width: 46,
                    height: 46,
                    minWidth: 46,
                    borderRadius: 9,
                    background: "#e7f8f7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon
                    name="clipboard check"
                    size="large"
                    style={{
                      color: "#00a8a8",
                      margin: 0,
                    }}
                  />
                </div>

                <div>
                  <Header
                    as="h2"
                    style={{
                      margin: 0,
                      color: "#172b4d",
                    }}
                  >
                    Protocolos de Mantención

                    <Header.Subheader
                      style={{
                        marginTop: 4,
                        color: "#6b778c",
                      }}
                    >
                      Configuración de pautas
                      técnicas para Servicio
                      Técnico
                    </Header.Subheader>
                  </Header>
                </div>
              </div>
            </Grid.Column>

            <Grid.Column
              width={4}
              textAlign="right"
            >
              <Button
                basic
                icon
                labelPosition="left"
                onClick={loadAll}
                loading={loading}
                disabled={saving}
              >
                <Icon name="refresh" />
                Actualizar
              </Button>
            </Grid.Column>
          </Grid>
        </Segment>

        {/* MENSAJES */}
        {toast.msg && (
          <Message
            positive={
              toast.type === "ok"
            }
            negative={
              toast.type === "err"
            }
            onDismiss={resetToast}
          >
            <Icon
              name={
                toast.type === "ok"
                  ? "check circle"
                  : "exclamation triangle"
              }
            />

            {toast.msg}
          </Message>
        )}

        {saving && (
          <Message icon info>
            <Icon
              name="circle notched"
              loading
            />

            <Message.Content>
              <Message.Header>
                Procesando...
              </Message.Header>

              Espera mientras se completa
              la operación.
            </Message.Content>
          </Message>
        )}

        {/* ETAPA 1 */}
        <Segment
          style={
            !cabeceraPendiente
              ? activeCardStyle
              : cardStyle
          }
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <Label
              circular
              color="teal"
              size="large"
            >
              1
            </Label>

            <div>
              <Header
                as="h3"
                style={{
                  margin: 0,
                  color: "#253858",
                }}
              >
                Definición del protocolo

                <Header.Subheader
                  style={{
                    marginTop: 3,
                    color: "#6b778c",
                  }}
                >
                  Selecciona el equipo y la
                  periodicidad de la pauta.
                </Header.Subheader>
              </Header>
            </div>
          </div>

          <Form>
            <Grid stackable>
              <Grid.Row>
                <Grid.Column
                  computer={5}
                  tablet={8}
                  mobile={16}
                >
                  <Form.Field>
                    <label>Familia</label>

                    <Dropdown
                      fluid
                      selection
                      search
                      placeholder="Selecciona familia"
                      options={
                        familiasOptions
                      }
                      value={nomFamilia}
                      disabled={
                        saving ||
                        cabeceraPendiente
                      }
                      onChange={(_, d) =>
                        setNomFamilia(
                          d.value
                        )
                      }
                      noResultsMessage="Sin coincidencias"
                    />
                  </Form.Field>
                </Grid.Column>

                <Grid.Column
                  computer={6}
                  tablet={8}
                  mobile={16}
                >
                  <Form.Field>
                    <label>
                      Tipo de equipamiento
                    </label>

                    <Dropdown
                      fluid
                      selection
                      search
                      placeholder={
                        nomFamilia
                          ? "Selecciona tipo"
                          : "Primero selecciona familia"
                      }
                      options={tiposOptions}
                      value={nomTipo}
                      disabled={
                        !nomFamilia ||
                        saving ||
                        cabeceraPendiente
                      }
                      onChange={(_, d) =>
                        setNomTipo(
                          d.value
                        )
                      }
                      noResultsMessage="Sin coincidencias"
                    />
                  </Form.Field>
                </Grid.Column>

                <Grid.Column
                  computer={5}
                  tablet={8}
                  mobile={16}
                >
                  <Form.Field>
                    <label>Programa</label>

                    <Dropdown
                      fluid
                      selection
                      search
                      placeholder="Selecciona programa"
                      options={
                        programasOptions
                      }
                      value={programa}
                      disabled={
                        saving ||
                        cabeceraPendiente
                      }
                      onChange={(_, d) =>
                        setPrograma(
                          d.value
                        )
                      }
                    />
                  </Form.Field>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row
                verticalAlign="middle"
              >
                <Grid.Column
                  computer={9}
                  tablet={16}
                  mobile={16}
                >
                  {cabeceraPendiente ? (
                    <Message
                      info
                      size="small"
                      style={{
                        margin: 0,
                      }}
                    >
                      <Icon name="edit outline" />

                      Protocolo en edición.
                      Continúa configurando
                      los ítems de la pauta.
                    </Message>
                  ) : (
                    <Message
                      size="small"
                      style={{
                        margin: 0,
                        background:
                          "#f8fafc",
                        color: "#52606d",
                      }}
                    >
                      <Icon name="info circle" />

                      La pauta define los
                      controles que deberá
                      ejecutar el técnico
                      durante la mantención.
                    </Message>
                  )}
                </Grid.Column>

                <Grid.Column
                  computer={7}
                  tablet={16}
                  mobile={16}
                  textAlign="right"
                >
                  <Button
                    basic
                    onClick={nuevo}
                    disabled={saving}
                  >
                    <Icon name="file outline" />
                    Nuevo
                  </Button>

                  {!cabeceraPendiente && (
                    <Button
                      color="teal"
                      onClick={
                        addCabProtocolo
                      }
                      loading={saving}
                      disabled={
                        !nomFamilia ||
                        !nomTipo ||
                        !programa
                      }
                    >
                      <Icon name="save" />
                      Crear protocolo
                    </Button>
                  )}
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Segment>

        {/* ETAPA 2 */}
        {cabeceraPendiente && (
          <Segment
            style={activeCardStyle}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 18,
              }}
            >
              <Label
                circular
                color="teal"
                size="large"
              >
                2
              </Label>

              <div>
                <Header
                  as="h3"
                  style={{
                    margin: 0,
                    color: "#253858",
                  }}
                >
                  Configuración de la pauta

                  <Header.Subheader
                    style={{
                      marginTop: 3,
                      color: "#6b778c",
                    }}
                  >
                    Agrega los controles que
                    debe ejecutar el técnico.
                  </Header.Subheader>
                </Header>
              </div>
            </div>

            <Segment
              secondary
              style={{
                borderRadius: 8,
                border:
                  "1px solid #e5e9ef",
                background: "#f8fafc",
                marginBottom: 18,
              }}
            >
              <Grid
                stackable
                verticalAlign="middle"
              >
                <Grid.Column width={12}>
                  <Form>
                    <Form.Field>
                      <label>
                        Buscar y agregar ítem
                      </label>

                      <Dropdown
                        fluid
                        selection
                        search
                        clearable
                        placeholder="Buscar ítem del protocolo..."
                        options={
                          itemOptions
                        }
                        value={
                          selectedItemId
                        }
                        disabled={saving}
                        onChange={(_, d) => {
                          const value =
                            d.value || "";

                          setSelectedItemId(
                            value
                          );

                          if (value) {
                            agregarItem(
                              value
                            );
                          }
                        }}
                        noResultsMessage="Sin coincidencias"
                      />
                    </Form.Field>
                  </Form>
                </Grid.Column>

                <Grid.Column
                  width={4}
                  textAlign="right"
                >
                  <Label
                    basic
                    color="teal"
                    size="large"
                  >
                    <Icon name="list" />
                    {protocolo.length}{" "}
                    {protocolo.length === 1
                      ? "ítem"
                      : "ítems"}
                  </Label>
                </Grid.Column>
              </Grid>
            </Segment>

            {protocolo.length === 0 ? (
              <Message info icon>
                <Icon name="clipboard outline" />

                <Message.Content>
                  <Message.Header>
                    Pauta sin ítems
                  </Message.Header>

                  Agrega los controles que
                  deberá ejecutar el técnico
                  durante la mantención.
                </Message.Content>
              </Message>
            ) : (
              renderCategorias(
                protocolo,
                true
              )
            )}

            <div
              style={{
                marginTop: 18,
                paddingTop: 16,
                borderTop:
                  "1px solid #e5e9ef",
                display: "flex",
                justifyContent:
                  "space-between",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  color: "#6b778c",
                  fontSize: 13,
                }}
              >
                <Icon name="info circle" />
                Revisa la pauta antes de
                confirmarla.
              </div>

              <Button
                color="teal"
                icon
                labelPosition="left"
                onClick={
                  confirmarProtocolo
                }
                loading={saving}
                disabled={
                  saving ||
                  protocolo.length === 0
                }
              >
                <Icon name="check" />
                Confirmar protocolo
              </Button>
            </div>
          </Segment>
        )}

        {/* ETAPA 3 */}
        <Segment style={cardStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 18,
            }}
          >
            <Label
              circular
              basic
              color="teal"
              size="large"
            >
              3
            </Label>

            <div>
              <Header
                as="h3"
                style={{
                  margin: 0,
                  color: "#253858",
                }}
              >
                Gestión de protocolos

                <Header.Subheader
                  style={{
                    marginTop: 3,
                    color: "#6b778c",
                  }}
                >
                  Continúa pautas pendientes
                  o consulta protocolos ya
                  confirmados.
                </Header.Subheader>
              </Header>
            </div>
          </div>

          <Grid stackable columns={2}>
            {/* PENDIENTES */}
            <Grid.Column>
              <Segment
                style={{
                  padding: 0,
                  borderRadius: 8,
                  overflow: "hidden",
                  boxShadow: "none",
                  border:
                    "1px solid #e5e9ef",
                }}
              >
                <div
                  style={{
                    padding:
                      "12px 14px",
                    background:
                      "#f8fafc",
                    borderBottom:
                      "1px solid #e5e9ef",
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                  }}
                >
                  <Header
                    as="h4"
                    style={{
                      margin: 0,
                      color: "#253858",
                    }}
                  >
                    <Icon
                      name="hourglass half"
                      style={{
                        color: "#f2c037",
                      }}
                    />

                    <Header.Content>
                      Protocolos por terminar
                    </Header.Content>
                  </Header>

                  <Label
                    circular
                    basic
                  >
                    {
                      protocolCabPend.length
                    }
                  </Label>
                </div>

                {protocolCabPend.length ===
                0 ? (
                  <Message
                    icon
                    style={{
                      margin: 12,
                      boxShadow: "none",
                    }}
                  >
                    <Icon name="check circle outline" />

                    <Message.Content>
                      <Message.Header>
                        Sin pendientes
                      </Message.Header>

                      No hay protocolos en
                      edición.
                    </Message.Content>
                  </Message>
                ) : (
                  <div
                    style={{
                      overflowX: "auto",
                      maxHeight: 360,
                    }}
                  >
                    <Table
                      compact="very"
                      celled
                      selectable
                      style={{
                        margin: 0,
                        minWidth: 600,
                      }}
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Familia
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Tipo
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Programa
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                            textAlign="center"
                          >
                            Acción
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {protocolCabPend.map(
                          (c) => (
                            <Table.Row
                              key={c.id}
                            >
                              <Table.Cell>
                                {c.familia}
                              </Table.Cell>

                              <Table.Cell>
                                {c.tipo}
                              </Table.Cell>

                              <Table.Cell>
                                <Label
                                  basic
                                  size="small"
                                >
                                  {
                                    c.programa
                                  }
                                </Label>
                              </Table.Cell>

                              <Table.Cell
                                textAlign="center"
                              >
                                <Button
                                  basic
                                  compact
                                  icon
                                  size="mini"
                                  color="teal"
                                  title="Continuar edición"
                                  onClick={() =>
                                    cargarPendiente(
                                      c
                                    )
                                  }
                                >
                                  <Icon
                                    name="edit outline"
                                    style={{
                                      margin: 0,
                                    }}
                                  />
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          )
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </Segment>
            </Grid.Column>

            {/* CONFIRMADOS */}
            <Grid.Column>
              <Segment
                style={{
                  padding: 0,
                  borderRadius: 8,
                  overflow: "hidden",
                  boxShadow: "none",
                  border:
                    "1px solid #e5e9ef",
                }}
              >
                <div
                  style={{
                    padding:
                      "12px 14px",
                    background:
                      "#f8fafc",
                    borderBottom:
                      "1px solid #e5e9ef",
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "center",
                  }}
                >
                  <Header
                    as="h4"
                    style={{
                      margin: 0,
                      color: "#253858",
                    }}
                  >
                    <Icon
                      name="check circle"
                      style={{
                        color: "#21ba45",
                      }}
                    />

                    <Header.Content>
                      Protocolos confirmados
                    </Header.Content>
                  </Header>

                  <Label
                    circular
                    basic
                  >
                    {
                      protocolCabConf.length
                    }
                  </Label>
                </div>

                {protocolCabConf.length ===
                0 ? (
                  <Message
                    icon
                    style={{
                      margin: 12,
                      boxShadow: "none",
                    }}
                  >
                    <Icon name="clipboard outline" />

                    <Message.Content>
                      <Message.Header>
                        Sin registros
                      </Message.Header>

                      No hay protocolos
                      confirmados.
                    </Message.Content>
                  </Message>
                ) : (
                  <div
                    style={{
                      overflowX: "auto",
                      maxHeight: 360,
                    }}
                  >
                    <Table
                      compact="very"
                      celled
                      selectable
                      style={{
                        margin: 0,
                        minWidth: 600,
                      }}
                    >
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Familia
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Tipo
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                          >
                            Programa
                          </Table.HeaderCell>

                          <Table.HeaderCell
                            style={
                              stickyHeader
                            }
                            textAlign="center"
                          >
                            Acción
                          </Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {protocolCabConf.map(
                          (c) => (
                            <Table.Row
                              key={c.id}
                            >
                              <Table.Cell>
                                {c.familia}
                              </Table.Cell>

                              <Table.Cell>
                                {c.tipo}
                              </Table.Cell>

                              <Table.Cell>
                                <Label
                                  basic
                                  size="small"
                                >
                                  {
                                    c.programa
                                  }
                                </Label>
                              </Table.Cell>

                              <Table.Cell
                                textAlign="center"
                              >
                                <Button
                                  basic
                                  compact
                                  icon
                                  size="mini"
                                  title="Ver protocolo"
                                  onClick={() =>
                                    verProtocoloConfirmado(
                                      c.id
                                    )
                                  }
                                  style={{
                                    background:
                                      "#fff",
                                    boxShadow:
                                      "none",
                                  }}
                                >
                                  <Icon
                                    name="eye"
                                    color="teal"
                                    style={{
                                      margin: 0,
                                    }}
                                  />
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          )
                        )}
                      </Table.Body>
                    </Table>
                  </div>
                )}
              </Segment>
            </Grid.Column>
          </Grid>
        </Segment>

        {/* MODAL VER */}
        <Modal
          open={verModalOpen}
          onClose={() =>
            setVerModalOpen(false)
          }
          size="large"
        >
          <Modal.Header>
            <Icon
              name="clipboard check"
              color="teal"
            />
            Protocolo de Mantención
          </Modal.Header>

          <Modal.Content scrolling>
            {verLoading ? (
              <Message icon>
                <Icon
                  name="circle notched"
                  loading
                />

                <Message.Content>
                  <Message.Header>
                    Procesando...
                  </Message.Header>

                  Cargando protocolo.
                </Message.Content>
              </Message>
            ) : verProt.length === 0 ? (
              <Message info icon>
                <Icon name="info circle" />

                <Message.Content>
                  <Message.Header>
                    Sin ítems
                  </Message.Header>

                  No se encontraron ítems
                  para este protocolo.
                </Message.Content>
              </Message>
            ) : (
              renderCategorias(verProt)
            )}
          </Modal.Content>

          <Modal.Actions>
            <Button
              basic
              onClick={() =>
                setVerModalOpen(false)
              }
            >
              <Icon name="close" />
              Cerrar
            </Button>
          </Modal.Actions>
        </Modal>

        {/* MODAL ELIMINAR */}
        <Modal
          size="tiny"
          open={deleteModalOpen}
          onClose={() => {
            if (!saving) {
              setDeleteModalOpen(false);
            }
          }}
        >
          <Modal.Header>
            <Icon
              name="trash alternate outline"
              color="red"
            />
            Eliminar ítem
          </Modal.Header>

          <Modal.Content>
            <Message warning>
              <Message.Header>
                ¿Eliminar este ítem?
              </Message.Header>

              El ítem será eliminado de la
              pauta que estás configurando.
            </Message>
          </Modal.Content>

          <Modal.Actions>
            <Button
              basic
              disabled={saving}
              onClick={() =>
                setDeleteModalOpen(false)
              }
            >
              Cancelar
            </Button>

            <Button
              color="red"
              loading={saving}
              onClick={borrarItem}
            >
              <Icon name="trash" />
              Eliminar
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    </Container>
  );
};

export default Protocolos;